import { useState, useRef, useEffect, ImgHTMLAttributes } from "react";
import { ImageOff } from "lucide-react";

interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
 /** If true, load eagerly (for LCP images) */
 eager?: boolean;
 /**
 * Low-quality image placeholder URL rendered blurred underneath the
 * final image, then cross-faded out once the sharp image decodes.
 * Prevents blank frames and reinforces layout stability.
 */
 placeholder?: string;
 /** Aspect ratio for the wrapper (e.g. "1 / 1", "4 / 5"). Prevents CLS
 * when the parent doesn't already reserve height. */
 aspectRatio?: string;
 /** Extra class for the wrapper element. */
 wrapperClassName?: string;
 /** Optional AVIF srcset. When provided (with or without webp), the image
 * is wrapped in a <picture> so the browser picks the smallest format it
 * can decode (AVIF ~30% smaller than WebP, WebP ~25% smaller than JPEG). */
 avifSrcSet?: string;
 /** Optional WebP srcset. Same picture-wrapping behaviour as `avifSrcSet`. */
 webpSrcSet?: string;
}

/**
 * Image component with:
 * - IntersectionObserver-based deferred loading (skipped when `eager`)
 * - Native `loading="lazy"` and `decoding="async"` fallbacks
 * - Optional low-quality blur-up placeholder cross-fade
 * - Optional intrinsic aspect ratio wrapper to eliminate CLS
 *
 * `width` / `height` / `sizes` / `srcSet` are forwarded to the underlying
 * `<img>` so callers can hint the browser about intrinsic size and
 * responsive candidates.
 */
const LazyImage = ({
 eager,
 src,
 alt,
 className = "",
 style,
 placeholder,
 aspectRatio,
 wrapperClassName = "",
 avifSrcSet,
 webpSrcSet,
 ...rest
}: LazyImageProps) => {
 const [loaded, setLoaded] = useState(false);
 const [errored, setErrored] = useState(false);
 const [inView, setInView] = useState(eager ?? false);
 const imgRef = useRef<HTMLImageElement>(null);

 useEffect(() => {
 if (eager || inView) return;
 const el = imgRef.current;
 if (!el) return;

 const observer = new IntersectionObserver(
 ([entry]) => {
 if (entry.isIntersecting) {
 setInView(true);
 observer.disconnect();
 }
 },
 { rootMargin: "200px" }
 );
 observer.observe(el);
 return () => observer.disconnect();
 }, [eager, inView]);

 const { srcSet: srcSetProp, sizes: sizesProp, fetchPriority, onLoad: onLoadProp, ...imgRest } = rest;
 const hasModernSources = Boolean(avifSrcSet || webpSrcSet);
 const imgEl = (
 <img
 ref={imgRef}
 {...imgRest}
 src={inView ? src : undefined}
 srcSet={inView ? srcSetProp : undefined}
 sizes={sizesProp}
 alt={alt}
 className={`transition-opacity duration-500 ${loaded && !errored ? "opacity-100" : "opacity-0"} ${className}`}
 style={style}
 loading={eager ? "eager" : "lazy"}
 decoding="async"
 fetchPriority={eager ? "high" : fetchPriority}
 onLoad={(e) => {
 setErrored(false);
 setLoaded(true);
 // Forward to caller so it can inspect naturalWidth/naturalHeight
 // (needed by ProductCard's hybrid cover/contain fit selector).
 onLoadProp?.(e);
 }}
 onError={() => setErrored(true)}
 />
 );
 // Wrap in <picture> only when a modern-format srcset was supplied. The
 // browser picks the first <source> it can decode (AVIF then WebP), and
 // falls back to the <img>'s origin-format srcset otherwise. Sources are
 // gated on `inView` so lazy behaviour is preserved: an off-screen
 // <source> with no srcSet contributes nothing to preload.
 const img = hasModernSources ? (
 <picture>
 {avifSrcSet && (
 <source type="image/avif" srcSet={inView ? avifSrcSet : undefined} sizes={sizesProp} />
 )}
 {webpSrcSet && (
 <source type="image/webp" srcSet={inView ? webpSrcSet : undefined} sizes={sizesProp} />
 )}
 {imgEl}
 </picture>
 ) : (
 imgEl
 );


 // Branded fallback shown when the image fails to load, replaces the ugly
 // native broken-image glyph with an on-brand muted card + icon.
 const fallback = errored ? (
 <span
 role="img"
 aria-label={typeof alt === "string" && alt ? alt : "Image unavailable"}
 className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground/70 px-4 text-center"
 >
 <ImageOff size={28} strokeWidth={1.5} aria-hidden="true" />
 <span className="font-body text-[10px] uppercase tracking-[0.2em]">
 Image unavailable
 </span>
 </span>
 ) : null;

 // Shared skeleton layer: on-brand shimmer that sits underneath the blur-up
 // placeholder and the sharp image, then fades out once the image decodes.
 // Keeps the grid feeling polished even before the tiny placeholder loads.
 const skeleton = (
 <span
 aria-hidden
 className={`absolute inset-0 bg-[linear-gradient(110deg, hsl(var(--muted))_20%, hsl(var(--muted-foreground)/0.08)_45%, hsl(var(--muted))_70%)] bg-[length:200%_100%] animate-shimmer transition-opacity duration-500 ${loaded || errored ? "opacity-0" : "opacity-100"}`}
 />
 );

 // If no placeholder and no aspect wrapper needed, wrap just to host the
 // skeleton overlay, bare <img> can't stack a sibling.
 if (!placeholder && !aspectRatio) {
 return (
 <span className={`relative block overflow-hidden ${wrapperClassName}`}>
 {skeleton}
 {img}
 {fallback}
 </span>
 );
 }

 return (
 <span
 className={`relative block overflow-hidden ${wrapperClassName}`}
 style={aspectRatio ? { aspectRatio } : undefined}
 >
 {skeleton}
 {placeholder && !errored && (
 <span
 aria-hidden
 className={`absolute inset-0 bg-center bg-cover scale-110 blur-xl transition-opacity duration-700 ${loaded ? "opacity-0" : "opacity-100"}`}
 style={{ backgroundImage: `url(${placeholder})` }}
 />
 )}
 {img}
 {fallback}
 </span>
 );
};

export default LazyImage;
